<template>
  <div class="card">
    <header class="card-header">
      <div class="d-flex">
        <strong class="align-self-center">16. Anexos</strong>
        <div class="ml-auto">
          <button class="btn btn-sm btn-secondary" @click="$emit('close')">Voltar</button>
        </div>
      </div>
    </header>

    <div class="card-body">
      <ul class="nav nav-tabs mb-3">
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: activeTab === 'documentos' }"
            href="#"
            @click.prevent="activeTab = 'documentos'"
          >Documentos</a>
        </li>
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: activeTab === 'perguntas' }"
            href="#"
            @click.prevent="activeTab = 'perguntas'"
          >Perguntas e Respostas</a>
        </li>
      </ul>

      <!-- ── Documentos ─────────────────────────────────────────────────── -->
      <div v-if="activeTab === 'documentos'">
        <div class="d-flex align-items-center mb-3">
          <label for="annex-upload-input" class="btn btn-outline-primary btn-sm mb-0 cursor-pointer">
            <i class="fa fa-upload mr-1"></i> Anexar documento
          </label>
          <input
            id="annex-upload-input"
            type="file"
            accept=".pdf,.docx"
            class="d-none"
            @change="onFileSelected"
          />
          <small class="text-muted ml-3">Apenas .pdf e .docx, um por vez</small>
        </div>

        <div v-if="uploading.length" class="mb-3">
          <div v-for="u in uploading" :key="u.name" class="small text-muted">
            <i class="fa fa-spinner fa-spin mr-1"></i> Enviando {{ u.name }}...
          </div>
        </div>

        <div v-if="loadingAnnexes" class="text-center py-4">
          <RotateSquare size="60px" class="loading-position animated fadeIn"></RotateSquare>
        </div>
        <div v-else-if="!annexes.length" class="text-muted text-center py-4">
          Nenhum documento anexado ainda.
        </div>
        <table v-else class="table table-sm">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Categoria</th>
              <th>Descrição</th>
              <th>Status</th>
              <th>Tamanho</th>
              <th>Data</th>
              <th class="text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="a in annexes" :key="a.id">
              <td>{{ a.filename }}</td>
              <td>{{ categoryLabel(a.category) }}</td>
              <td>
                <span v-if="a.description" class="text-truncate d-inline-block" style="max-width:260px" :title="a.description">
                  {{ a.description }}
                </span>
                <span v-else class="badge badge-warning" title="Sem descrição — o assistente de busca não conseguirá escolher este documento com precisão.">
                  <i class="fa fa-exclamation-triangle mr-1"></i>Sem descrição
                </span>
              </td>
              <td>
                <span :class="'badge ' + statusBadgeClass(a.status)">{{ statusLabel(a.status) }}</span>
                <i
                  v-if="a.status === 'failed' && a.error_message"
                  class="fa fa-info-circle text-danger ml-1"
                  :title="a.error_message"
                ></i>
              </td>
              <td>{{ formatSize(a.size_bytes) }}</td>
              <td>{{ formatDate(a.created_at) }}</td>
              <td class="text-right">
                <button
                  class="btn btn-sm btn-outline-primary mr-1"
                  title="Editar categoria/descrição"
                  @click="openEdit(a)"
                >
                  <i class="fa fa-pencil-alt"></i>
                </button>
                <button
                  v-if="a.status === 'failed'"
                  class="btn btn-sm btn-outline-warning mr-1"
                  title="Reprocessar"
                  @click="reprocess(a)"
                >
                  <i class="fa fa-redo"></i>
                </button>
                <button
                  v-if="a.status === 'ready'"
                  class="btn btn-sm btn-outline-secondary mr-1"
                  title="Baixar"
                  @click="download(a)"
                >
                  <i class="fa fa-download"></i>
                </button>
                <button class="btn btn-sm btn-outline-danger" title="Excluir" @click="remove(a)">
                  <i class="fa fa-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ── Perguntas e Respostas ──────────────────────────────────────── -->
      <div v-else>
        <div class="d-flex mb-3">
          <input
            v-model="questionInput"
            type="text"
            class="form-control mr-2"
            placeholder="Faça uma pergunta sobre os documentos anexados..."
            :disabled="asking"
            @keyup.enter="askQuestion"
          />
          <button
            class="btn btn-primary text-nowrap"
            :disabled="asking || !questionInput.trim()"
            @click="askQuestion"
          >
            <i v-if="asking" class="fa fa-spinner fa-spin mr-1"></i>
            Perguntar
          </button>
        </div>

        <div v-if="loadingMessages" class="text-center py-4">
          <RotateSquare size="60px" class="loading-position animated fadeIn"></RotateSquare>
        </div>
        <div v-else-if="!messages.length" class="text-muted text-center py-4">
          Nenhuma pergunta feita ainda. Pergunte algo sobre os documentos anexados.
        </div>
        <div v-else class="row qa-layout">
          <!-- Lista de perguntas já feitas -->
          <div class="col-4 col-lg-3 qa-list-col">
            <div class="list-group qa-list">
              <a
                v-for="m in messagesDesc"
                :key="m.id"
                href="#"
                class="list-group-item list-group-item-action"
                :class="{ active: selectedMessageId === m.id }"
                @click.prevent="selectedMessageId = m.id"
              >
                <div class="text-truncate">{{ m.question }}</div>
                <small :class="selectedMessageId === m.id ? 'text-white-50' : 'text-muted'">{{ formatDate(m.created_at) }}</small>
              </a>
            </div>
          </div>

          <!-- Pergunta selecionada -->
          <div class="col-8 col-lg-9 qa-detail-col">
            <div v-if="selectedMessage" class="qa-detail">
              <div class="qa-bubble qa-question">
                <strong>Pergunta:</strong> {{ selectedMessage.question }}
              </div>
              <div class="qa-bubble qa-answer mt-2">
                <div class="qa-answer-html" v-html="renderMarkdown(selectedMessage.answer)"></div>
                <div v-if="groupedSources(selectedMessage.sources).length" class="qa-sources mt-3 pt-2">
                  <div class="small text-muted mb-1"><i class="fa fa-book mr-1"></i>Fontes:</div>
                  <div
                    v-for="(g, idx) in groupedSources(selectedMessage.sources)"
                    :key="idx"
                    class="small"
                  >
                    <i class="fa fa-file-pdf-o text-muted mr-1"></i>{{ g.filename }}
                    <span v-if="g.pages.length" class="text-muted">— p. {{ g.pages.join(", ") }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de anexo (upload) / edição de categoria+descrição -->
    <b-modal
      v-model="annexModalAberto"
      :title="pendingFile ? 'Anexar documento' : 'Editar documento'"
      @ok="confirmAnnexModal"
      @hidden="closeAnnexModal"
      ok-title="Salvar"
      cancel-title="Cancelar"
      size="lg"
    >
      <div class="form-group">
        <label><strong>Arquivo</strong></label>
        <div class="form-control-plaintext">{{ pendingFile ? pendingFile.name : editingAnnex && editingAnnex.filename }}</div>
      </div>
      <div class="form-group">
        <label><strong>Categoria</strong></label>
        <select v-model="annexForm.category" class="form-control">
          <option value="lei_municipal">Lei municipal</option>
          <option value="plano_diretor">Plano diretor</option>
          <option value="codigo_ambiental">Código ambiental</option>
          <option value="outro">Outro</option>
        </select>
      </div>
      <div class="form-group">
        <label><strong>Descrição</strong> <span class="text-danger">*</span></label>
        <b-form-textarea
          v-model="annexForm.description"
          rows="3"
          placeholder="Ex: Lei municipal nº 1234/2020 — dispõe sobre poda, supressão e plantio de árvores no perímetro urbano, incluindo distâncias mínimas de rede elétrica e passeio público."
        />
        <small class="text-muted d-block mt-1">
          A descrição é <strong>obrigatória</strong> porque o assistente de busca (aba "Perguntas e
          Respostas") não lê o conteúdo dos documentos de antemão — ele decide em qual documento
          procurar a resposta com base nesta descrição, antes de buscar dentro do arquivo. Uma
          descrição vaga ou ausente faz o assistente escolher o documento errado (ou nenhum) e
          responder "não encontrei", mesmo quando a informação existe em algum anexo. Descreva do
          que trata o documento e, se possível, os assuntos/temas específicos que ele cobre.
        </small>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { marked } from "marked";
import DOMPurify from "dompurify";
import AnnexService from "../../servico/AnnexService";
import RotateSquare from "../../components/RotateSquare";

marked.setOptions({ gfm: true, breaks: true });

const CATEGORY_LABELS = {
  lei_municipal: "Lei municipal",
  plano_diretor: "Plano diretor",
  codigo_ambiental: "Código ambiental",
  outro: "Outro"
};

const STATUS_LABELS = {
  pending_upload: "Aguardando upload",
  processing: "Processando",
  ready: "Pronto",
  failed: "Falhou"
};

const STATUS_BADGE_CLASSES = {
  pending_upload: "badge-light",
  processing: "badge-warning",
  ready: "badge-success",
  failed: "badge-danger"
};

const POLL_INTERVAL_MS = 4000;

export default {
  name: "PmauAnnexes",
  components: { RotateSquare },
  props: {
    contaId: { type: String, required: true }
  },
  data() {
    return {
      activeTab: "documentos",
      annexes: [],
      loadingAnnexes: false,
      uploading: [],
      pollTimer: null,
      annexModalAberto: false,
      pendingFile: null,
      editingAnnex: null,
      annexForm: { category: "outro", description: "" },
      messages: [],
      loadingMessages: false,
      questionInput: "",
      asking: false,
      selectedMessageId: null
    };
  },
  computed: {
    messagesDesc() {
      return [...this.messages].reverse();
    },
    selectedMessage() {
      return this.messages.find((m) => m.id === this.selectedMessageId) || null;
    }
  },
  beforeMount() {
    this.loadAnnexes();
    this.loadMessages();
  },
  beforeDestroy() {
    this.clearPolling();
  },
  methods: {
    renderMarkdown(text) {
      return DOMPurify.sanitize(marked.parse(text || ""));
    },
    groupedSources(sources) {
      if (!sources || !sources.length) return [];
      const byFile = new Map();
      sources.forEach((s) => {
        if (!byFile.has(s.filename)) byFile.set(s.filename, new Set());
        if (s.page_number) byFile.get(s.filename).add(s.page_number);
      });
      return Array.from(byFile.entries()).map(([filename, pages]) => ({
        filename,
        pages: Array.from(pages).sort((a, b) => a - b)
      }));
    },
    categoryLabel(category) {
      return CATEGORY_LABELS[category] || category || "-";
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status;
    },
    statusBadgeClass(status) {
      return STATUS_BADGE_CLASSES[status] || "badge-light";
    },
    formatSize(bytes) {
      if (!bytes) return "-";
      const mb = bytes / (1024 * 1024);
      return mb >= 1 ? mb.toFixed(1) + " MB" : (bytes / 1024).toFixed(0) + " KB";
    },
    formatDate(iso) {
      if (!iso) return "-";
      return new Date(iso).toLocaleString("pt-BR");
    },

    loadAnnexes() {
      this.loadingAnnexes = true;
      AnnexService.ListAnnexes(this.contaId)
        .then((r) => {
          this.annexes = r.data;
          this.schedulePollingIfNeeded();
        })
        .catch((e) => this.$notify({ data: [e.message], type: "error" }))
        .finally(() => (this.loadingAnnexes = false));
    },

    schedulePollingIfNeeded() {
      const hasProcessing = this.annexes.some((a) => a.status === "processing");
      if (hasProcessing && !this.pollTimer) {
        this.pollTimer = setInterval(() => {
          AnnexService.ListAnnexes(this.contaId).then((r) => {
            this.annexes = r.data;
            if (!this.annexes.some((a) => a.status === "processing")) {
              this.clearPolling();
            }
          });
        }, POLL_INTERVAL_MS);
      } else if (!hasProcessing) {
        this.clearPolling();
      }
    },

    clearPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    onFileSelected(event) {
      const file = (event.target.files || [])[0];
      event.target.value = "";
      if (!file) return;
      const ext = file.name.toLowerCase();
      if (!ext.endsWith(".pdf") && !ext.endsWith(".docx")) {
        this.$notify({ data: ["Apenas arquivos .pdf e .docx são aceitos."], type: "error" });
        return;
      }
      this.pendingFile = file;
      this.editingAnnex = null;
      this.annexForm = { category: "outro", description: "" };
      this.annexModalAberto = true;
    },

    openEdit(annex) {
      this.pendingFile = null;
      this.editingAnnex = annex;
      this.annexForm = { category: annex.category || "outro", description: annex.description || "" };
      this.annexModalAberto = true;
    },

    closeAnnexModal() {
      this.pendingFile = null;
      this.editingAnnex = null;
      this.annexForm = { category: "outro", description: "" };
    },

    confirmAnnexModal(evento) {
      evento.preventDefault();
      if (!this.annexForm.description.trim()) {
        this.$notify({ data: ["A descrição do documento é obrigatória."], type: "warn" });
        return;
      }

      if (this.pendingFile) {
        this.uploadFile(this.pendingFile, this.annexForm.description.trim(), this.annexForm.category);
        this.annexModalAberto = false;
      } else if (this.editingAnnex) {
        AnnexService.UpdateAnnex(this.editingAnnex.id, {
          category: this.annexForm.category,
          description: this.annexForm.description.trim()
        })
          .then(() => {
            this.$notify({ data: ["Documento atualizado."], type: "success" });
            this.annexModalAberto = false;
            this.loadAnnexes();
          })
          .catch((e) => {
            const msg = (e.response && e.response.data && e.response.data.detail) || e.message;
            this.$notify({ data: [msg], type: "error" });
          });
      }
    },

    uploadFile(file, description, category) {
      this.uploading.push({ name: file.name });

      let annexId = null;
      AnnexService.RequestUploadUrl(this.contaId, file.name, file.size, description, category)
        .then((r) => {
          annexId = r.data.annex_id;
          return AnnexService.UploadToS3(r.data.upload_url, file);
        })
        .then(() => AnnexService.ConfirmUpload(annexId))
        .then(() => {
          this.$notify({ data: ["Documento enviado, processando..."], type: "success" });
          this.loadAnnexes();
        })
        .catch((e) => {
          const msg = (e.response && e.response.data && e.response.data.detail) || e.message;
          this.$notify({ data: [msg], type: "error" });
        })
        .finally(() => {
          this.uploading = this.uploading.filter((u) => u.name !== file.name);
        });
    },

    reprocess(annex) {
      AnnexService.ReprocessAnnex(annex.id)
        .then(() => {
          this.$notify({ data: ["Reprocessamento iniciado."], type: "success" });
          this.loadAnnexes();
        })
        .catch((e) => this.$notify({ data: [e.message], type: "error" }));
    },

    download(annex) {
      AnnexService.GetDownloadUrl(annex.id)
        .then((r) => window.open(r.data.download_url, "_blank"))
        .catch((e) => this.$notify({ data: [e.message], type: "error" }));
    },

    remove(annex) {
      if (!confirm('Excluir o documento "' + annex.filename + '"?')) return;
      AnnexService.DeleteAnnex(annex.id)
        .then(() => {
          this.annexes = this.annexes.filter((a) => a.id !== annex.id);
        })
        .catch((e) => this.$notify({ data: [e.message], type: "error" }));
    },

    loadMessages() {
      this.loadingMessages = true;
      AnnexService.ListQaMessages(this.contaId)
        .then((r) => {
          this.messages = r.data;
          if (this.messages.length) {
            this.selectedMessageId = this.messages[this.messages.length - 1].id;
          }
        })
        .catch((e) => this.$notify({ data: [e.message], type: "error" }))
        .finally(() => (this.loadingMessages = false));
    },

    askQuestion() {
      const question = this.questionInput.trim();
      if (!question) return;
      this.asking = true;
      AnnexService.AskQuestion(this.contaId, question)
        .then((r) => {
          this.messages.push(r.data);
          this.selectedMessageId = r.data.id;
          this.questionInput = "";
        })
        .catch((e) => {
          const msg = (e.response && e.response.data && e.response.data.detail) || e.message;
          this.$notify({ data: [msg], type: "error" });
        })
        .finally(() => (this.asking = false));
    }
  }
};
</script>

<style scoped>
.qa-bubble {
  padding: 10px 14px;
  border-radius: 8px;
}
.qa-question {
  background-color: #f0f4f8;
}
.qa-answer {
  background-color: #eef7ee;
}
.qa-layout {
  min-height: 300px;
}
.qa-list {
  max-height: 520px;
  overflow-y: auto;
}
.qa-list .list-group-item {
  cursor: pointer;
}
.qa-sources {
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}
</style>

<style>
.qa-answer-html p {
  margin-bottom: 0.75rem;
}
.qa-answer-html p:last-child {
  margin-bottom: 0;
}
.qa-answer-html ul,
.qa-answer-html ol {
  padding-left: 1.4rem;
  margin-bottom: 0.75rem;
}
.qa-answer-html h1,
.qa-answer-html h2,
.qa-answer-html h3,
.qa-answer-html h4 {
  font-size: 1rem;
  font-weight: 600;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}
</style>
